import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
    MatFormField,
    MatHint,
    MatLabel,
    MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { DataFormModel, SelectSuffixAction } from '@shared/components/data-form/data-form.model';
import { FormDrawerComponent } from '@shared/components/page-layout/drawers/form-drawer/form-drawer.component';
import { FormDrawerRequest } from '@shared/components/page-layout/drawers/form-drawer/form-drawer.type';
import { PageLayoutService } from '@shared/components/page-layout/page-layout.service';
import { Role } from '@shared/enums/roles.enum';
import { enumToDescriptedArray } from '@shared/functions/utils.type';

@Component({
	selector: 'formly-select-action-type',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		FormlyModule,
		MatFormField,
		MatHint,
		MatLabel,
		MatSelect,
		MatOption,
		MatSuffix,
		MatIconButton,
		MatIcon,
		MatTooltip,
	],
	template: `
        <mat-form-field class="w-full">
            <mat-label>{{ props.label }}</mat-label>
            <mat-select
                [formControl]="formControl"
                [formlyAttributes]="field"
                [required]="props.required"
                [multiple]="props.multiple"
            >
                @for (item of props.options || []; track item?.value) {
                    <mat-option
                        [value]="item?.value"
                        [disabled]="item?.disabled === true || (disabledMatchValue() !== null && disabledMatchValue() !== undefined && disabledMatchValue() !== 'none' && item?.value === disabledMatchValue())"
                    >
                        {{ item?.label }}
                    </mat-option>
                }
            </mat-select>

            @if (props?.suffixAction?.buttonKey && !props?.hideSuffixAction) {
                <button
                    matSuffix
                    mat-icon-button
                    type="button"
                    [attr.aria-label]="props?.suffixAction?.label || 'Action'"
                    [matTooltip]="props?.suffixAction?.label || ''"
                    (click)="onSuffixAction()"
                >
                    <mat-icon
                        [svgIcon]="props?.suffixAction?.svgIcon || null"
                        >{{ props?.suffixAction?.icon }}</mat-icon
                    >
                </button>
            }

            @if (props.description) {
                <mat-hint>{{ props.description }}</mat-hint>
            }
        </mat-form-field>
    `,
})
export class SelectActionTypeComponent extends FieldType<FieldTypeConfig> implements OnInit {
	private layoutService = inject(PageLayoutService);
	private destroyRef = inject(DestroyRef);
	disabledMatchValue = signal<string | number | null>(null);

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	ngOnInit(): void {
		const matchField = this.props?.disableMatchField as string;
		if (matchField && this.form) {
			const control = this.form.get(matchField);
			if (control) {
				this.disabledMatchValue.set(control.value as string);
				control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
					this.disabledMatchValue.set(value);
				});
			}
		}
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	onSuffixAction(): void {
		switch ((this.props?.suffixAction as SelectSuffixAction)?.buttonKey) {
			case 'addSalesUser':
				this.openAddSalesUserDrawer();
				break;
			case 'addRoom':
				this.openAddRoomDrawer();
				break;
		}
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------
	private openAddRoomDrawer(): void {
		this.layoutService.navigateParentDrawer({
			component: FormDrawerComponent,
			smallDrawer: true,
			title: 'Add Room',
			data: {
				formUrl: 'project-folder',
				skipApi: true,
				model: { addRoom: true, addItemDraftModel: { ...((this.model as DataFormModel) || {}) } },
				idField: 'id',
				dictionary: { projectFolderId: this.props.options || [] },
			} as FormDrawerRequest,
		});
	}

	private openAddSalesUserDrawer(): void {
		this.layoutService.navigateParentDrawer({
			component: FormDrawerComponent,
			smallDrawer: true,
			title: 'Add Sales User',
			data: {
				dictionary: {
					source: enumToDescriptedArray(Role),
					salesRepresentativeUserId: this.props.options || [],
				},
				formUrl: 'admin-user',
				skipApi: true,
				keepOpen: false,
				model: {
					recaptchaToken: 'default-token',
					source: Role.Sales,
					slug: 'default-slug',
					addStorage: false,
					hideSourceField: true,
					returnToCreateJob: (this.props?.suffixAction as SelectSuffixAction)?.returnToCreateJob === true,
					returnToAdminUser: (this.props?.suffixAction as SelectSuffixAction)?.returnToAdminUser === true,
					createJobUserId: (this.model as DataFormModel)?.userId ?? null,
					createJobDraftModel: (this.props?.suffixAction as SelectSuffixAction)?.returnToCreateJob === true ? { ...((this.model as DataFormModel) || {}) } : null,
					adminUserDraftModel: (this.props?.suffixAction as SelectSuffixAction)?.returnToAdminUser === true ? { ...((this.model as DataFormModel) || {}) }: null,
				},
				idField: 'id',
			} as FormDrawerRequest,
		});
	}
}
